import { ReactNode, useState } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import Button, { ButtonVariantType } from '@dealroadshow/uikit-button';
import Tooltip, { ITooltipProps } from '@dealroadshow/uikit-tooltip';
import linkStyles from '@dealroadshow/uikit-styles/links.module.scss';
import IconCopy from '@dealroadshow/uikit-icon/IconCopy';
import { IconComponentType } from '@dealroadshow/uikit-icon';
import { useCopyToClipboard } from '@dealroadshow/uikit-hooks/useCopyToClipboard';

import styles from './copyToClipboardButton.module.scss';

interface IProps {
  content: string,
  dataTest?: string,
  isOutlineButtons?: boolean,
  className?: string,
  copyElemClassName?: string,
  copiedElemClassName?: string,
  labelBeforeCopy?: string,
  labelAfterCopy?: string,
  isDisabled?: boolean,
  isMobile?: boolean,
  customStyles?: string,
  withTooltip?: boolean,
  copyTooltip?: ReactNode,
  copiedTooltip?: ReactNode,
  handleCopy?: () => void,
  copiedMessageDelay?: number,
  icon?: IconComponentType,
  hideOnTooltipMouseEnter?: boolean,
  offsetOptions?: ITooltipProps['offsetOptions'],
  copiedIcon?: IconComponentType,
}

/**
 * @deprecated This component has been chosen for migration to UIKit, meaning any further changes or updates
 * must be completed within the migration process.
 * Note: CopyToClipboardButton should be merged with CopyToClipboard, before moving
 * @see https://www.notion.so/finsight-group/Component-Migration-Process-f4475950481d429ba0dc450d0bb0cb8b
 */
const CopyToClipboardButton = ({
  dataTest = 'copyToClipboard',
  copyTooltip = 'Copy to clipboard',
  copiedTooltip = 'Copied to Clipboard!',
  handleCopy = noop,
  copiedMessageDelay = 2000,
  icon = IconCopy,
  hideOnTooltipMouseEnter = true,
  offsetOptions,
  copiedIcon,
  ...otherProps
}: IProps) => {
  const props = {
    dataTest,
    copyTooltip,
    copiedTooltip,
    handleCopy,
    copiedMessageDelay,
    icon,
    copiedIcon,
    hideOnTooltipMouseEnter,
    offsetOptions,
    ...otherProps,
  };

  const [isCopied, setIsCopied] = useState(false);

  const onSuccess = () => {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), props.copiedMessageDelay);
    props.handleCopy();
  };

  const [copy] = useCopyToClipboard({ onSuccess });

  const IconComponent = isCopied && copiedIcon ? copiedIcon : icon;
  return (
    <Tooltip
      offsetOptions={ props.offsetOptions }
      hideOnTooltipMouseEnter={ props.hideOnTooltipMouseEnter }
      disabled={ !(props.isOutlineButtons || props.withTooltip) || props.isMobile }
      content={ isCopied ? props.copiedTooltip : props.copyTooltip }
    >
      <div
        // @ts-ignore
        className={ cn({ [props.className]: !props.isOutlineButtons }) }
        data-test={ props.dataTest }
      >
        <span data-clipboard-text={ props.content }>
          <Button
            variant={ props.isOutlineButtons ? ButtonVariantType.outline : ButtonVariantType.text }
            disabled={ props.isDisabled || isCopied }
            className={ cn(props.customStyles,
              {
                [linkStyles.link]: !props.isOutlineButtons,
                // @ts-ignore
                [props.copyElemClassName]: !isCopied,
                // @ts-ignore
                [props.copiedElemClassName]: isCopied,
              },
            ) }
            onClick={ () => copy(props.content) }
            dataTest={ `${ props.dataTest }Button` }
          >
            <IconComponent
              className={ cn({ [styles.iconOutline]: props.isOutlineButtons }) }
            />
            <span>{ !isCopied ? props.labelBeforeCopy : props.labelAfterCopy }</span>
          </Button>
        </span>
      </div>
    </Tooltip>
  );
};

export default CopyToClipboardButton;
